/**
 * HolidayCard Component
 * Always-visible horizontal banner showing the current month's holidays, events & special occasions
 * Supports custom days added by owners/directors
 * Includes School Calendar access button
 */
import { useState, useEffect, useRef, memo, useMemo } from "react";
import { CalendarHeart, Plus, X, Trash2, Edit2, GraduationCap, Upload, Download, FileText, Loader2, ZoomIn, ZoomOut, RefreshCw, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { format } from "date-fns";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.mjs`;

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// PDF Viewer component - renders PDF inline using PDF.js (memoized to prevent re-renders)
const PDFViewer = memo(({ calendarId, title, token, filename, onRefresh }) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const pdfUrl = useMemo(() => 
    `${API}/school-calendars/${calendarId}/pdf?token=${encodeURIComponent(token)}&r=${refreshKey}`,
    [calendarId, token, refreshKey]
  );
  const [numPages, setNumPages] = useState(null);
  // Load saved zoom from localStorage or default to 80%
  const [scale, setScale] = useState(() => {
    const saved = localStorage.getItem('schoolCalendarZoom');
    return saved ? parseFloat(saved) : 0.8;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [zoomInput, setZoomInput] = useState(Math.round(scale * 100).toString());

  // Update zoom input when scale changes
  useEffect(() => {
    setZoomInput(Math.round(scale * 100).toString());
  }, [scale]);

  const handleZoomChange = (newScale) => {
    const clamped = Math.max(0.25, Math.min(2.0, newScale));
    setScale(clamped);
  };

  const handleZoomInputBlur = () => {
    const value = parseInt(zoomInput, 10);
    if (!isNaN(value) && value >= 25 && value <= 200) {
      handleZoomChange(value / 100);
    } else {
      setZoomInput(Math.round(scale * 100).toString());
    }
  };

  const handleZoomInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleZoomInputBlur();
    }
  };

  const handleSaveZoom = () => {
    localStorage.setItem('schoolCalendarZoom', scale.toString());
    toast.success(`Zoom saved at ${Math.round(scale * 100)}%`);
  };

  const handleRefresh = () => {
    setLoading(true);
    setError(false);
    setRefreshKey(k => k + 1);
    if (onRefresh) onRefresh();
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setLoading(false);
  };

  const onDocumentLoadError = () => {
    setError(true);
    setLoading(false);
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-12 px-8 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mb-4">
          <FileText className="w-8 h-8 text-red-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-800 mb-1">{filename || `${title} Calendar.pdf`}</h3>
        <p className="text-gray-500 text-sm mb-5">Unable to preview</p>
        <Button size="sm" onClick={() => window.open(pdfUrl, '_blank')} className="bg-blue-600 hover:bg-blue-700">
          <FileText className="w-4 h-4 mr-1" />
          Open PDF
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-3 py-2 bg-white border-b shrink-0">
        <span className="text-xs text-gray-600 font-medium truncate max-w-[120px]">{filename}</span>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleZoomChange(scale - 0.1)} title="Zoom out 10%">
            <ZoomOut className="w-3 h-3" />
          </Button>
          <input
            type="text"
            value={zoomInput}
            onChange={(e) => setZoomInput(e.target.value)}
            onBlur={handleZoomInputBlur}
            onKeyDown={handleZoomInputKeyDown}
            className="w-12 h-6 text-xs text-center border rounded px-1"
            title="Enter zoom % (25-200)"
          />
          <span className="text-xs text-gray-400">%</span>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleZoomChange(scale + 0.1)} title="Zoom in 10%">
            <ZoomIn className="w-3 h-3" />
          </Button>
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={handleSaveZoom} title="Save zoom preference">
            Save
          </Button>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={handleRefresh} title="Refresh PDF">
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => window.open(pdfUrl, '_blank')} title="Open in new tab">
            Open
          </Button>
        </div>
      </div>
      
      {/* PDF Content */}
      <div className="flex-1 overflow-auto bg-gray-200 p-2">
        {loading && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            <span className="ml-2 text-gray-600 text-sm">Loading...</span>
          </div>
        )}
        <Document
          file={pdfUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading=""
        >
          <div className="flex flex-col items-center gap-2">
            {Array.from(new Array(numPages), (el, index) => (
              <Page
                key={`page_${index + 1}`}
                pageNumber={index + 1}
                scale={scale}
                className="shadow-md"
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            ))}
          </div>
        </Document>
      </div>
    </div>
  );
});

// Pre-loaded standard holidays by month (1-12)
const STANDARD_HOLIDAYS = {
  1: [ // January
    { emoji: "🎆", name: "New Year's Day", day: 1 },
    { emoji: "🕊️", name: "MLK Jr. Day", day: 19 },
    { emoji: "❄️", name: "Winter Season", day: 1, endDay: null, note: "Jan-Mar" },
  ],
  2: [ // February
    { emoji: "❤️", name: "Valentine's Day", day: 14 },
    { emoji: "🏛️", name: "Presidents' Day", day: 16 },
  ],
  3: [ // March
    { emoji: "📚", name: "Read Across America Day", day: 2 },
    { emoji: "🍀", name: "St. Patrick's Day", day: 17 },
    { emoji: "🌸", name: "First Day of Spring", day: 20 },
  ],
  4: [ // April
    { emoji: "🐰", name: "Easter", day: 5 },
    { emoji: "🌎", name: "Earth Day", day: 22 },
  ],
  5: [ // May
    { emoji: "🍎", name: "Teacher Appreciation", day: 4, endDay: 8 },
    { emoji: "🌷", name: "Mother's Day", day: 10 },
    { emoji: "🎖️", name: "Memorial Day", day: 25 },
  ],
  6: [ // June
    { emoji: "👔", name: "Father's Day", day: 21 },
    { emoji: "☀️", name: "First Day of Summer", day: 21 },
    { emoji: "🎓", name: "Graduation Season", day: 1, note: "All Month" },
  ],
  7: [ // July
    { emoji: "🎆", name: "Independence Day", day: 4 },
    { emoji: "🍦", name: "National Ice Cream Day", day: 19 },
    { emoji: "☀️", name: "Summer Season", day: 1, note: "All Month" },
  ],
  8: [ // August
    { emoji: "😊", name: "Friendship Day", day: 2 },
    { emoji: "🎒", name: "Back to School", day: 1, note: "All Month" },
  ],
  9: [ // September
    { emoji: "🛠️", name: "Labor Day", day: 7 },
    { emoji: "👵", name: "Grandparents Day", day: 13 },
    { emoji: "🍂", name: "First Day of Fall", day: 22 },
  ],
  10: [ // October
    { emoji: "🚓", name: "Fire Safety Week", day: 4, endDay: 10 },
    { emoji: "🌎", name: "Indigenous Peoples' Day", day: 12 },
    { emoji: "🎃", name: "Halloween", day: 31 },
  ],
  11: [ // November
    { emoji: "🗳️", name: "Election Day", day: 3 },
    { emoji: "🎖️", name: "Veterans Day", day: 11 },
    { emoji: "🦃", name: "Thanksgiving", day: 26 },
  ],
  12: [ // December
    { emoji: "🕎", name: "Hanukkah", day: 6, endDay: 14 },
    { emoji: "🎄", name: "Christmas", day: 25 },
    { emoji: "🕯️", name: "Kwanzaa", day: 26, endDay: 31, note: "thru Jan 1" },
    { emoji: "🎆", name: "New Year's Eve", day: 31 },
  ],
};

// Available emoji icons for custom days
const EMOJI_OPTIONS = [
  "🎂", "🎈", "🎉", "⭐", "🎵", "🎨", "🚌", "📖", "🏆", "🎭",
  "🌈", "🦋", "🐾", "🌻", "🎪", "🎬", "🎤", "🎯", "🎁", "💝",
  "👑", "🌟", "🎊", "🎶", "📸", "🏅", "🎡", "🧁", "🍕", "🎠",
  "🎓", "🏃", "🎺", "🎻", "🎹", "🎸", "⚽", "🏀", "🎾", "🌺",
];

const HolidayCard = () => {
  const { user, token } = useAuth();
  const [customHolidays, setCustomHolidays] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    emoji: "⭐",
    startDate: "",
    endDate: "",
    repeatYearly: false,
  });
  
  // School Calendar State
  const [showSchoolCalendarModal, setShowSchoolCalendarModal] = useState(false);
  const [schoolCalendars, setSchoolCalendars] = useState([]);
  const [selectedCalendar, setSelectedCalendar] = useState(null);
  const [showAddDistrictModal, setShowAddDistrictModal] = useState(false);
  const [newDistrictName, setNewDistrictName] = useState("");
  const [uploadingCalendar, setUploadingCalendar] = useState(false);
  const schoolFileInputRef = useRef(null);
  const [hasShownInstructions, setHasShownInstructions] = useState(false);

  // Show instructions toast when modal opens
  useEffect(() => {
    if (showSchoolCalendarModal && !hasShownInstructions) {
      setTimeout(() => {
        toast.info(
          "Accepts: PDF, PNG, JPG • Use -/+ to zoom • Type % directly • Click Save to remember\n\n📝 Word/Excel docs: Save as PDF first",
          { duration: 6000 }
        );
      }, 500);
      setHasShownInstructions(true);
    }
  }, [showSchoolCalendarModal, hasShownInstructions]);

  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1; // 1-12
  const currentYear = currentDate.getFullYear();
  const monthName = format(currentDate, "MMMM");
  
  // Auth headers for API calls
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};
  
  // Check if owner/co-owner
  const isOwnerOrCoOwner = user?.role === 'owner' || 
                           user?.role?.toLowerCase() === 'co-owner' ||
                           user?.role?.toLowerCase() === 'coowner';

  // Fetch custom holidays and school calendars on mount
  useEffect(() => {
    if (token) {
      fetchCustomHolidays();
      fetchSchoolCalendars();
    }
  }, [token]);

  const fetchCustomHolidays = async () => {
    try {
      const response = await axios.get(`${API}/holidays/custom`, { headers: authHeaders });
      setCustomHolidays(response.data.holidays || []);
    } catch (error) {
      console.error("Error fetching custom holidays:", error);
    }
  };
  
  const fetchSchoolCalendars = async () => {
    try {
      const response = await axios.get(`${API}/school-calendars`, { headers: authHeaders });
      const calendars = response.data.calendars || [];
      setSchoolCalendars(calendars);
      if (calendars.length > 0 && !selectedCalendar) {
        setSelectedCalendar(calendars[0]);
      }
    } catch (error) {
      console.error("Error fetching school calendars:", error);
    }
  };
  
  const handleSchoolCalendarUpload = async (event, calendarId = null) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error("Please upload a PDF file");
      return;
    }
    
    if (file.size > 15 * 1024 * 1024) {
      toast.error("File size must be less than 15MB");
      return;
    }
    
    setUploadingCalendar(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('district_name', newDistrictName || selectedCalendar?.districtName || "School District");
    formData.append('uploaded_by', user?.name || user?.email || 'Unknown');
    
    try {
      if (calendarId) {
        // Update existing
        await axios.put(`${API}/school-calendars/${calendarId}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data', ...authHeaders }
        });
        toast.success("Calendar updated!");
      } else {
        // Add new
        await axios.post(`${API}/school-calendars`, formData, {
          headers: { 'Content-Type': 'multipart/form-data', ...authHeaders }
        });
        toast.success("School calendar added!");
        setNewDistrictName("");
        setShowAddDistrictModal(false);
      }
      fetchSchoolCalendars();
    } catch (error) {
      console.error("Error uploading calendar:", error);
      toast.error("Failed to upload calendar");
    } finally {
      setUploadingCalendar(false);
      if (schoolFileInputRef.current) {
        schoolFileInputRef.current.value = '';
      }
    }
  };
  
  const handleDeleteCalendar = async (calendarId) => {
    if (!window.confirm("Delete this school calendar?")) return;
    
    try {
      console.log("Deleting calendar:", calendarId);
      await axios.delete(`${API}/school-calendars/${calendarId}`, { headers: authHeaders });
      toast.success("Calendar deleted");
      fetchSchoolCalendars();
      setSelectedCalendar(null);
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete calendar: " + (error.response?.data?.detail || error.message));
    }
  };

  // Get holidays for current month
  const getMonthHolidays = () => {
    const standardForMonth = STANDARD_HOLIDAYS[currentMonth] || [];
    
    // Filter custom holidays for current month
    const customForMonth = customHolidays.filter(h => {
      // Parse date without timezone conversion - split the date string
      const [year, month, day] = h.date.split('-').map(Number);
      return month === currentMonth && 
             (h.repeatYearly || year === currentYear);
    }).map(h => {
      // Parse dates without timezone conversion
      const [, , day] = h.date.split('-').map(Number);
      const endDay = h.endDate ? h.endDate.split('-').map(Number)[2] : null;
      return {
        emoji: h.emoji,
        name: h.name,
        day: day,
        endDay: endDay,
        isCustom: true,
        id: h.id,
      };
    });

    // Combine and sort by day
    return [...standardForMonth, ...customForMonth].sort((a, b) => a.day - b.day);
  };

  const formatDay = (day, endDay, note) => {
    if (note) return note;
    if (endDay) return `${day}${getOrdinalSuffix(day)}-${endDay}${getOrdinalSuffix(endDay)}`;
    return `${day}${getOrdinalSuffix(day)}`;
  };

  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return "th";
    switch (day % 10) {
      case 1: return "st";
      case 2: return "nd";
      case 3: return "rd";
      default: return "th";
    }
  };

  const handleAddOrEdit = async () => {
    if (!formData.name || !formData.startDate) {
      toast.error("Please enter a name and date");
      return;
    }

    try {
      const holidayData = {
        name: formData.name,
        emoji: formData.emoji,
        date: formData.startDate,
        endDate: formData.endDate || null,
        repeatYearly: formData.repeatYearly,
      };

      if (editingHoliday) {
        await axios.put(`${API}/holidays/custom/${editingHoliday.id}`, holidayData, { headers: authHeaders });
        toast.success("Special day updated!");
      } else {
        await axios.post(`${API}/holidays/custom`, holidayData, { headers: authHeaders });
        toast.success("Special day added!");
      }

      fetchCustomHolidays();
      resetForm();
    } catch (error) {
      console.error("Error saving holiday:", error);
      toast.error("Failed to save special day");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Remove this special day?")) return;
    
    try {
      await axios.delete(`${API}/holidays/custom/${id}`, { headers: authHeaders });
      toast.success("Special day removed");
      fetchCustomHolidays();
    } catch (error) {
      console.error("Error deleting holiday:", error);
      toast.error("Failed to remove special day");
    }
  };

  const handleEdit = (holiday) => {
    setEditingHoliday(holiday);
    const startDate = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(holiday.day).padStart(2, '0')}`;
    const endDate = holiday.endDay ? `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(holiday.endDay).padStart(2, '0')}` : "";
    setFormData({
      name: holiday.name,
      emoji: holiday.emoji,
      startDate: startDate,
      endDate: endDate,
      repeatYearly: false,
    });
    setShowAddModal(true);
  };

  const resetForm = () => {
    setFormData({ name: "", emoji: "⭐", startDate: "", endDate: "", repeatYearly: false });
    setEditingHoliday(null);
    setShowAddModal(false);
  };

  const holidays = getMonthHolidays();

  return (
    <>
      {/* Holiday Banner - 3D raised effect with shadow - HIDDEN ON PRINT */}
      <div className="relative print:hidden">
        {/* Shadow layer behind */}
        <div 
          className="absolute inset-0 rounded-xl"
          style={{
            background: 'rgba(59,130,246,0.15)',
            transform: 'translateY(4px)',
            filter: 'blur(4px)'
          }}
        />
        <div 
          className="relative w-full rounded-xl overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(239,246,255,0.98) 0%, rgba(224,231,255,0.95) 50%, rgba(239,246,255,0.98) 100%)',
            boxShadow: '0 6px 20px rgba(59,130,246,0.25), 0 2px 6px rgba(59,130,246,0.15), inset 0 1px 0 rgba(255,255,255,0.9)',
            border: '1px solid rgba(59,130,246,0.3)'
          }}
        >
        <div className="flex items-center gap-2 px-4 py-2">
          {/* School Calendar Button - Moved to left */}
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={() => setShowSchoolCalendarModal(true)}
                  className="flex-shrink-0 p-2 rounded-full bg-green-500 hover:bg-green-600 transition-colors"
                >
                  <GraduationCap className="w-4 h-4 text-white" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="bg-green-800 text-white border-green-700">
                School District Calendars
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Icon, Title & Month */}
          <div className="flex items-center gap-2 pr-3 border-r border-blue-300 flex-shrink-0">
            <CalendarHeart className="w-5 h-5 text-blue-600" />
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="font-semibold text-blue-800 text-sm cursor-help flex items-center gap-1">
                    Holiday & Special Events
                    <Info className="w-3.5 h-3.5 text-blue-400" />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="bg-blue-800 text-white border-blue-700 max-w-[200px]">
                  <p className="text-xs">This area updates automatically on the 1st of each month</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <span className="text-blue-600 text-sm font-medium">| {monthName}</span>
          </div>

          {/* Scrollable Holiday List */}
          <div className="flex-1 overflow-x-auto scrollbar-hide">
            <div className="flex items-center gap-1.5 flex-wrap">
              {holidays.length === 0 ? (
                <span className="text-xs text-blue-600">No events this month</span>
              ) : (
                holidays.map((holiday, idx) => (
                  <div 
                    key={`${holiday.name}-${idx}`}
                    className={`flex items-center gap-1 px-2 py-1 rounded-full whitespace-nowrap text-xs flex-shrink-0 group ${
                      holiday.isCustom 
                        ? 'bg-purple-100 border border-purple-200 text-purple-800' 
                        : 'bg-white/80 border border-blue-200 text-blue-800'
                    }`}
                  >
                    <span className="text-sm">{holiday.emoji}</span>
                    <span className="font-medium">{holiday.name}</span>
                    <span className="text-blue-600">({formatDay(holiday.day, holiday.endDay, holiday.note)})</span>
                    {holiday.isCustom && (
                      <div className="flex gap-0.5 ml-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => handleEdit(holiday)}
                          className="p-0.5 rounded hover:bg-purple-200 transition-colors"
                        >
                          <Edit2 className="w-3 h-3 text-purple-600" />
                        </button>
                        <button
                          onClick={() => handleDelete(holiday.id)}
                          className="p-0.5 rounded hover:bg-red-200 transition-colors"
                        >
                          <Trash2 className="w-3 h-3 text-red-500" />
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Add Button - Available to everyone */}
          <button
            onClick={() => setShowAddModal(true)}
            className="flex-shrink-0 p-2 rounded-full bg-blue-500 hover:bg-blue-600 transition-colors"
            title="Add Special Day"
          >
            <Plus className="w-4 h-4 text-white" />
          </button>
        </div>
        </div>
      </div>

      {/* School Calendar Modal - Hidden on print */}
      <Dialog open={showSchoolCalendarModal} onOpenChange={setShowSchoolCalendarModal}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-hidden flex flex-col print:hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-green-600" />
              School District Calendars
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-hidden flex flex-col space-y-4">
            {/* District Selector */}
            <div className="flex items-center gap-3">
              <select
                value={selectedCalendar?.id || ""}
                onChange={(e) => {
                  const cal = schoolCalendars.find(c => c.id === e.target.value);
                  setSelectedCalendar(cal);
                }}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                {schoolCalendars.length === 0 ? (
                  <option value="">No calendars uploaded</option>
                ) : (
                  schoolCalendars.map((cal) => (
                    <option key={cal.id} value={cal.id}>
                      {cal.districtName}
                    </option>
                  ))
                )}
              </select>
              
              {/* Add District Button - Available to everyone */}
              <Button
                onClick={() => setShowAddDistrictModal(true)}
                className="bg-green-600 hover:bg-green-700 text-white"
                size="sm"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add District
              </Button>
            </div>

            {/* PDF Viewer or Empty State */}
            <div className="flex-1 border rounded-lg overflow-hidden bg-gray-50 min-h-[400px]">
              {selectedCalendar ? (
                <PDFViewer 
                  calendarId={selectedCalendar.id} 
                  title={selectedCalendar.districtName} 
                  token={token}
                  filename={selectedCalendar.filename}
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-gray-500 py-12">
                  <GraduationCap className="w-16 h-16 text-gray-300 mb-4" />
                  <p className="text-lg font-medium">No School Calendars</p>
                  <p className="text-sm">Click "Add District" to upload a school calendar</p>
                </div>
              )}
            </div>

            {/* Actions for selected calendar */}
            {selectedCalendar && (
              <div className="flex items-center justify-between pt-3 border-t">
                <div className="text-sm text-gray-500">
                  <span className="font-medium">{selectedCalendar.districtName}</span>
                  <span className="mx-2">•</span>
                  <span>Updated {new Date(selectedCalendar.updatedAt).toLocaleDateString()}</span>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setShowSchoolCalendarModal(false)}
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    size="sm"
                  >
                    Close
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      // Use backend endpoint for download
                      const downloadUrl = `${API}/school-calendars/${selectedCalendar.id}/pdf?token=${encodeURIComponent(token)}`;
                      const link = document.createElement('a');
                      link.href = downloadUrl;
                      link.download = selectedCalendar.filename;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      toast.success("Download started");
                    }}
                  >
                    <Download className="w-4 h-4 mr-1" />
                    Download
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = '.pdf,.png,.jpg,.jpeg';
                      input.onchange = async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        
                        const formData = new FormData();
                        formData.append('file', file);
                        formData.append('district_name', selectedCalendar.districtName);
                        formData.append('uploaded_by', user?.name || user?.email || 'Unknown');
                        
                        try {
                          toast.loading("Uploading...", { id: "replace-cal" });
                          await axios.put(`${API}/school-calendars/${selectedCalendar.id}`, formData, {
                            headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
                          });
                          toast.success("Calendar replaced! Click Refresh to see changes.", { id: "replace-cal" });
                          // Refresh the calendar list
                          await fetchSchoolCalendars();
                        } catch (error) {
                          console.error("Replace error:", error);
                          toast.error("Failed to replace: " + (error.response?.data?.detail || error.message), { id: "replace-cal" });
                        }
                      };
                      input.click();
                    }}
                  >
                    <Upload className="w-4 h-4 mr-1" />
                    Replace
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:bg-red-50 hover:text-red-700"
                    data-testid="delete-calendar-btn"
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      
                      const calId = selectedCalendar?.id;
                      const calName = selectedCalendar?.districtName;
                      
                      if (!calId) {
                        toast.error("No calendar selected");
                        return;
                      }
                      
                      // Delete directly without confirmation
                      try {
                        toast.loading("Deleting...", { id: "delete-cal" });
                        await axios.delete(`${API}/school-calendars/${calId}`, { 
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        toast.success(`"${calName}" deleted`, { id: "delete-cal" });
                        setSelectedCalendar(null);
                        await fetchSchoolCalendars();
                      } catch (error) {
                        toast.error("Delete failed: " + (error.response?.data?.detail || error.message), { id: "delete-cal" });
                      }
                    }}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            )}
            
            {/* Close Button when no calendar selected */}
            {!selectedCalendar && (
              <div className="flex justify-end pt-3 border-t">
                <Button
                  onClick={() => setShowSchoolCalendarModal(false)}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  size="sm"
                >
                  Close
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Add New District Modal - Hidden on print */}
      <Dialog open={showAddDistrictModal} onOpenChange={() => { setShowAddDistrictModal(false); setNewDistrictName(""); }}>
        <DialogContent className="sm:max-w-md print:hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-green-600" />
              Add School District Calendar
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="districtName">School District Name</Label>
              <Input
                id="districtName"
                value={newDistrictName}
                onChange={(e) => setNewDistrictName(e.target.value)}
                placeholder="e.g., Wilson School District"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Upload Calendar PDF</Label>
              <div
                className="mt-2 border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-green-400 transition-colors cursor-pointer"
                onClick={() => {
                  if (!newDistrictName.trim()) {
                    toast.error("Please enter the district name first");
                    return;
                  }
                  schoolFileInputRef.current?.click();
                }}
              >
                <input
                  ref={schoolFileInputRef}
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg"
                  onChange={(e) => handleSchoolCalendarUpload(e)}
                  className="hidden"
                />
                {uploadingCalendar ? (
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-10 h-10 text-green-600 animate-spin" />
                    <p className="text-gray-600">Uploading...</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3">
                    <FileText className="w-10 h-10 text-gray-400" />
                    <p className="text-gray-600">Click to upload PDF or Image</p>
                    <p className="text-sm text-gray-400">PDF, PNG, JPG • Max 15MB</p>
                    <p className="text-xs text-gray-400">Word/Excel? Save as PDF first</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddDistrictModal(false); setNewDistrictName(""); }}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Modal - Calendar Style like Staff Calendar - Hidden on print */}
      <Dialog open={showAddModal} onOpenChange={resetForm}>
        <DialogContent className="sm:max-w-md print:hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarHeart className="w-5 h-5 text-blue-600" />
              {editingHoliday ? "Edit Special Day" : "Add Special Day"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Name */}
            <div>
              <Label htmlFor="name">Event Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Center Spirit Week"
                className="mt-1"
              />
            </div>

            {/* Date Selection - Like Staff Calendar */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="startDate">Start Date</Label>
                <Input
                  id="startDate"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="endDate">End Date (optional)</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            {/* Emoji Picker */}
            <div>
              <Label>Choose Icon</Label>
              <div className="mt-2 p-3 border rounded-lg bg-gray-50 max-h-32 overflow-y-auto">
                <div className="grid grid-cols-10 gap-1">
                  {EMOJI_OPTIONS.map((emoji) => (
                    <button
                      key={emoji}
                      type="button"
                      onClick={() => setFormData({ ...formData, emoji })}
                      className={`text-xl p-1 rounded hover:bg-blue-100 transition-colors ${
                        formData.emoji === emoji ? 'bg-blue-200 ring-2 ring-blue-400' : ''
                      }`}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Repeat Yearly */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="repeatYearly"
                checked={formData.repeatYearly}
                onChange={(e) => setFormData({ ...formData, repeatYearly: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500"
              />
              <Label htmlFor="repeatYearly" className="text-sm font-normal cursor-pointer">
                Repeat this event every year
              </Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={resetForm}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddOrEdit}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {editingHoliday ? "Save Changes" : "Add Special Day"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default HolidayCard;
